// @flow
import React from 'react';
import moment from 'moment';
import LongTermDurationPicker from '../../LazyLongTermDurationPicker';
import NativeDatePicker from '../DatePicker/NativeDatePicker';
import Chevron from '../../../svg/chevron.svg';

import '../../../../static/scss/components/bookingDate.scss';
import useMonthlyRollingOnly from '../../../hooks/useMonthlyRollingOnly';

type Props = {
  dateFrom: string,
  dateTo: string,
  dateFromRaw: string,
  dateToRaw: string,
  modifier: string,
  datePickerTarget: boolean,
  datePickerVisible: boolean,
  isValid: boolean,
  onClickHandler: () => void,
  onNativeDateChange: () => void,
  browser: any,
  isMonthly: boolean,
  monthlyDuration: string,
  onMonthlyChange: () => {},
  showLessThanOneMonth?: boolean
};

// if no date provided, the element will be "disabled" ( different styling )
const BookingDate = ({
  dateFrom,
  dateTo,
  dateFromRaw,
  dateToRaw,
  modifier = 'single',
  datePickerTarget = false,
  datePickerVisible = false,
  isValid = true,
  onClickHandler = () => {},
  onNativeDateChange = () => {},
  browser,
  isMonthly,
  monthlyDuration,
  onMonthlyChange,
  showLessThanOneMonth = false
}: Props) => {
  const placeholder = `Select a date${!isMonthly ? ' & time' : ''}`;
  const innerDateFrom = {
    date: dateFrom || placeholder,
    modifier: dateFrom ? 'enabled' : 'disabled'
  };

  const innerDateTo = {
    date: dateTo || placeholder,
    modifier: dateTo ? 'enabled' : 'disabled'
  };

  const baseClass = 'c-booking-date__date';
  const validClass = `${baseClass}--${isValid ? 'valid' : 'invalid'}`;

  const rollingOnly = useMonthlyRollingOnly();

  return (
    <div
      className={`c-booking-date c-booking-date--${modifier} ${
        isMonthly ? 'c-booking-date--is-monthly' : ''
      }`}
    >
      <NativeDatePicker
        value={dateFromRaw}
        min={moment().add(-1, 'day')}
        max={moment().add(2, 'years')}
        className="c-booking-date__native-input c-booking-date__native-input--from"
        output={false}
        name="from"
        onChange={timestamp => {
          onNativeDateChange('arriving', timestamp);
        }}
        onClick={() => {
          const shouldOpenNonNativePicker = false;
          onClickHandler('from', shouldOpenNonNativePicker);
        }}
        shouldRender={browser.is.extraSmall}
        showTimePicker={!isMonthly}
      >
        <div
          onClick={onClickHandler.bind(this, 'from')}
          className={`${baseClass} ${baseClass}--${modifier} ${
            datePickerVisible && datePickerTarget === 'from' ? 'is-active' : ''
          } ${baseClass}--${innerDateTo.modifier} ${validClass}`}
        >
          {modifier === 'single' && (
            <h6>
              <span className="c-booking-date__native-input__span">
                {isMonthly ? 'Starting on' : 'Arriving on'}
              </span>
            </h6>
          )}
          <span
            className="c-booking-date__date-display"
            data-cy="booking-start-date"
          >
            {innerDateTo.date}
          </span>
          <Chevron className="c-booking-date__native-input__chevron" />
        </div>
      </NativeDatePicker>
      {isMonthly ? (
        <LongTermDurationPicker
          selected={monthlyDuration}
          onOptionSelect={onMonthlyChange}
          includesLessThanOneMonth={showLessThanOneMonth}
          rollingOnly={rollingOnly}
        />
      ) : (
        <NativeDatePicker
          value={dateToRaw}
          min={dateFromRaw}
          max={moment().add(2, 'years')}
          className="c-booking-date__native-input c-booking-date__native-input--to"
          output={false}
          name="to"
          onChange={timestamp => {
            onNativeDateChange('leaving', timestamp);
          }}
          onClick={() => {
            const shouldOpenNonNativePicker = false;
            onClickHandler('to', shouldOpenNonNativePicker);
          }}
          shouldRender={browser.is.extraSmall}
          showTimePicker
        >
          <div
            onClick={onClickHandler.bind(this, 'to')}
            className={`${baseClass} ${baseClass}--${modifier} ${
              datePickerVisible && datePickerTarget === 'to' ? 'is-active' : ''
            } ${baseClass}--${innerDateFrom.modifier} ${validClass}`}
          >
            {modifier === 'single' && (
              <h6>
                <span className="c-booking-date__native-input__span">
                  Leaving on
                </span>
              </h6>
            )}
            <span className="c-booking-date__date-display">
              {innerDateFrom.date}
            </span>
            <Chevron className="c-booking-date__native-input__chevron" />
          </div>
        </NativeDatePicker>
      )}
    </div>
  );
};

export default BookingDate;
